import { useApi } from '@backstage/core-plugin-api';
import { visitsApiRef } from '@backstage/plugin-home';
import { Progress, ResponseErrorPanel, Link } from '@backstage/core-components';
import { Flex, Text } from '@backstage/ui';
import useAsync from 'react-use/esm/useAsync';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { HomeCard } from './HomeCard';

// plugin-home's own HomePageRecentlyVisited draws an InfoCard, which nests
// inside the widget wrapper's card. The card-less `Content` the stock widgets
// use has no subpath export, so render the list directly off visitsApiRef.
const LIMIT = 8;

function relative(timestamp: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export const RecentlyVisitedContent = () => {
  const visitsApi = useApi(visitsApiRef);
  const { value, loading, error } = useAsync(
    () =>
      visitsApi.list({
        limit: LIMIT,
        orderBy: [{ field: 'timestamp', direction: 'desc' }],
      }),
    [visitsApi],
  );

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const visits = value ?? [];
  if (visits.length === 0) {
    return (
      <Text variant="body-small" color="secondary">
        Pages you open will show up here.
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="2">
      {visits.map(visit => (
        <Flex key={visit.id} justify="between" align="center" gap="2">
          <Link to={visit.pathname}>{visit.name}</Link>
          <Text variant="body-small" color="secondary">
            {relative(visit.timestamp)}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

// Shares plugin-home's extension id, so this replaces the stock widget.
export const recentlyVisitedWidget = HomePageWidgetBlueprint.make({
  name: 'recently-visited',
  params: {
    name: 'RecentlyVisited',
    title: 'Recently Visited',
    description: 'Pages you opened most recently',
    components: () =>
      import('./RecentlyVisitedContent').then(m => ({
        Content: m.RecentlyVisitedContent,
      })),
    componentProps: { Renderer: HomeCard },
    layout: {
      width: { defaultColumns: 6, minColumns: 3 },
      height: { defaultRows: 5, minRows: 3 },
    },
  },
});
